import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import Button from '../Button';
import Modal from '../Modal';
import RateLine from '../RateLine';
import TextArea from '../TextArea';
import getRatingMarks from './getRatingMarks';
import getRatingData from './getRatingData';

import styles from '../../../styles/views/components/RateProjectModal.module.scss';

const RateProjectModal = ({
  isShown,
  userRole,
  callback,
  onRate,
  closeRateModal,
  ratingType,
}) => {
  const [ratingComment, setRatingComment] = useState('');
  const [ratingMarks, setRatingMarks] = useState(getRatingMarks(ratingType, userRole));

  const onChangeRatingComment = useCallback((e) => {
    setRatingComment(e.target.value);
  }, []);

  const onChangeRating = (ratingName) => (value) => {
    setRatingMarks(
      ratingMarks.map((rating) => (rating.key === ratingName ? ({ ...rating, value }) : rating)),
    );
  };
  const isSomeRatingEmpty = useMemo(() => ratingMarks.some((rating) => rating.value === 0), [ratingMarks]);

  const calculateTotalRating = () => (
    isSomeRatingEmpty
      ? 0
      : Number((ratingMarks.reduce((acc, r) => (acc + r.value), 0) / ratingMarks.length).toFixed(1))
  );

  return (
    <Modal isShown={isShown} onClose={closeRateModal} modalExtraClass={styles.modal}>
      <div className={styles.container}>
        <h2>{t(`rateProjectModal.${ratingType}.title`, { ns: i18next.Namespace.COMMON })}</h2>
        <hr />
        <div className={styles.ratingSection}>
          <p>{t(`rateProjectModal.${ratingType}.${userRole}.modalDescription`, { ns: i18next.Namespace.COMMON })}</p>
          <p className={styles.infoBox}>
            {t(`rateProjectModal.${ratingType}.${userRole}.infoBox`, { ns: i18next.Namespace.COMMON })}
          </p>
          {getRatingData(ratingType, userRole).map((rating) => (
            <div key={rating.name} className={styles.ratingItem}>
              <h3>{t(rating.label, { ns: i18next.Namespace.COMMON })}</h3>
              <p>{t(rating.description, { ns: i18next.Namespace.COMMON })}</p>
              <span className={styles.ratingStars}>
                <RateLine
                  key={rating.name}
                  name={rating.name}
                  isDisabled={rating.isDisabled}
                  rate={rating.name === 'overallRating'
                    ? calculateTotalRating()
                    : ratingMarks.find((r) => r.key === rating.name)?.value}
                  onChange={onChangeRating(rating.name)}
                />
              </span>
            </div>
          ))}
        </div>
        <TextArea
          name="comment"
          label={t('button.comment', { ns: i18next.Namespace.COMMON })}
          placeholder={t(
            `rateProjectModal.${ratingType}.${userRole}.commentPlaceholder`,
            { ns: i18next.Namespace.COMMON },
          )}
          value={ratingComment}
          onChange={onChangeRatingComment}
          maxLength={320}
          extraStyles={{ width: '560px', height: '88px' }}
          className={styles.commentsBlock}
        />
        <div className={styles.buttonsSection}>
          <Button
            id="rateProjectPrimaryAction"
            color="gold"
            variant="primary"
            value={t(`rateProjectModal.${ratingType}.primaryBtn`, { ns: i18next.Namespace.COMMON })}
            onClick={() => {
              onRate(ratingMarks, ratingComment);
              callback();
            }}
            isDisabled={isSomeRatingEmpty}
            tooltip={isSomeRatingEmpty ? t('tooltips.fillOutAllFields', { ns: i18next.Namespace.COMMON }) : ''}
          />
        </div>
      </div>
    </Modal>
  );
};

RateProjectModal.propTypes = {
  isShown: PropTypes.bool.isRequired,
  userRole: PropTypes.string.isRequired,
  callback: PropTypes.func,
  onRate: PropTypes.func.isRequired,
  closeRateModal: PropTypes.func.isRequired,
  ratingType: PropTypes.string.isRequired,
};

RateProjectModal.defaultProps = {
  callback: () => {},
};

export default RateProjectModal;
